// Small client-side timezone helper for the booking flow.
// Studio operates on Pacific Time (America/Los_Angeles). This component
// surfaces that clearly and shows the viewer's local equivalent when they
// pick a time so out-of-state clients don't book the wrong slot.
import { useEffect, useMemo, useState } from "react";
import { Globe } from "lucide-react";

const STUDIO_TZ = "America/Los_Angeles";

function detectLocalTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || STUDIO_TZ;
  } catch {
    return STUDIO_TZ;
  }
}

function shortTzLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === "timeZoneName")?.value;
    return name || tz;
  } catch {
    return tz;
  }
}

// Given "YYYY-MM-DD" + "HH:MM" interpreted in Pacific Time, return a friendly
// string in the viewer's local timezone (e.g. "Mon, Jul 7 · 6:00 PM EDT").
function formatInLocal(date, time, localTz) {
  if (!date || !time) return "";
  // Build a date in Pacific Time by using its naive ISO and asking Intl to convert.
  // We construct an ISO with no offset, then parse via a Date and treat the
  // hour as Pacific by computing the offset for that date.
  try {
    const [y, m, d] = date.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    // Use the well-known trick: create UTC date, then compute Pacific offset.
    const utc = new Date(Date.UTC(y, m - 1, d, hh, mm));
    const ptFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: STUDIO_TZ,
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
    const ptParts = ptFormatter.formatToParts(utc);
    const ptH = Number(ptParts.find((p) => p.type === "hour").value);
    const ptM = Number(ptParts.find((p) => p.type === "minute").value);
    // Offset between desired wall-clock PT and what UTC math produced
    const drift = (hh - ptH) * 60 + (mm - ptM);
    const real = new Date(utc.getTime() + drift * 60 * 1000);

    return new Intl.DateTimeFormat("en-US", {
      timeZone: localTz,
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(real);
  } catch {
    return "";
  }
}

export function TimezoneNotice({ compact = false }) {
  const local = useMemo(detectLocalTz, []);
  const isPacific = local === STUDIO_TZ;
  const localLabel = shortTzLabel(local);

  if (isPacific) {
    return (
      <div
        data-testid="tz-notice"
        className={`text-xs leading-relaxed bg-[#F2EBD3] border border-[#D9CFA0] rounded-md px-4 py-2.5 text-[#0F1F4E]/80 font-light flex items-center gap-2 ${
          compact ? "" : "mt-1"
        }`}
      >
        <Globe className="w-3.5 h-3.5 shrink-0" />
        All times shown in <span className="font-medium">Pacific (PT)</span> — studio
        located in Sacramento, CA.
      </div>
    );
  }

  return (
    <div
      data-testid="tz-notice"
      className={`text-xs leading-relaxed bg-[#F2EBD3] border border-[#D9CFA0] rounded-md px-4 py-2.5 text-[#0F1F4E]/80 font-light flex items-center gap-2 ${
        compact ? "" : "mt-1"
      }`}
    >
      <Globe className="w-3.5 h-3.5 shrink-0" />
      Studio time zone is <span className="font-medium">Pacific (PT)</span>. Your
      device is in <span className="font-medium">{localLabel}</span> — the form
      will show your local equivalent below.
    </div>
  );
}

export function LocalTimePreview({ date, time }) {
  const [local] = useState(detectLocalTz);
  if (!date || !time) return null;
  if (local === STUDIO_TZ) return null;
  const preview = formatInLocal(date, time, local);
  if (!preview) return null;
  return (
    <p
      data-testid="tz-local-preview"
      className="text-xs text-[#0F1F4E]/70 font-light mt-2"
    >
      → That's <span className="font-medium">{preview}</span> in your timezone.
    </p>
  );
}

export const PACIFIC_TZ = STUDIO_TZ;
